import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import { useFilters } from '../hooks/useFilters';
import { useLivePrices } from '../hooks/useLivePrices';
import './WidgetCustomizerPage.css';

/* ── Inline SVG Icons ── */
const IconBase = ({ children, size = 24, className = '' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    className={className}>
    {children}
  </svg>
);

const ExternalLinkIcon = (p) => (
  <IconBase {...p}>
    <path d="M15 3h6v6"/><path d="M10 14 21 3"/>
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
  </IconBase>
);

const InfoIcon = (p) => (
  <IconBase {...p}>
    <circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>
  </IconBase>
);

const BitcoinIcon = (p) => (
  <IconBase {...p}>
    <path d="M11.3 5c1.2-.2 2.4-.3 3.6-.3 2.5 0 4.6 1.4 4.6 3.8 0 1.9-1.2 3.2-2.8 3.6 2 .3 3.6 1.9 3.6 4.1 0 2.8-2.6 4.3-5.4 4.3-1.2 0-2.4-.1-3.6-.3"/>
    <path d="M7 5h4.3"/><path d="M7 22h4.3"/>
    <path d="M9.5 2v20"/><path d="M14.5 13v9"/><path d="M13.5 2v7"/>
  </IconBase>
);

/* ── Mock fallback data ── */
const MOCK_TRADES = {
  whales: [
    { id: 'mock-1', dollarSize: 24150000, side: 'buy', timestamp: Date.now() - 120000, price: 66840.20 },
    { id: 'mock-2', dollarSize: 18400000, side: 'sell', timestamp: Date.now() - 840000, price: 67450.50 },
    { id: 'mock-3', dollarSize: 12200000, side: 'buy', timestamp: Date.now() - 1680000, price: 66920.15 },
    { id: 'mock-4', dollarSize: 9500000, side: 'buy', timestamp: Date.now() - 2700000, price: 66710.00 },
  ],
  liqs: [
    { id: 'mock-5', dollarSize: 1240000, side: 'sell', timestamp: Date.now() - 300000, price: 67820.40 },
    { id: 'mock-6', dollarSize: 845000, side: 'buy', timestamp: Date.now() - 1140000, price: 66540.80 },
    { id: 'mock-7', dollarSize: 480000, side: 'sell', timestamp: Date.now() - 1440000, price: 67310.25 },
    { id: 'mock-8', dollarSize: 315000, side: 'buy', timestamp: Date.now() - 1860000, price: 66420.10 },
  ],
};

/* ── Helpers ── */
function formatDollar(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function formatTimeAgo(timestamp) {
  const ms = Date.now() - (typeof timestamp === 'number' ? timestamp : new Date(timestamp).getTime());
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function tradeSide(side) {
  return side === 'buy' ? 'long' : 'short';
}

/* ── Widget Embed Page (rendered inside iframe) ── */
function WidgetEmbedPage() {
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'dark';
  const [activeTab, setActiveTab] = useState('best');

  // Real data hooks
  const { filters } = useFilters();
  const { prices } = useLivePrices();
  const {
    whaleTrades: liveWhaleTrades,
    liquidations: liveLiquidations,
    historicalTrades,
  } = useWebSocket(filters, { enabled: true, playSounds: false });

  // Split historical trades by type
  const historicalWhales = useMemo(() =>
    historicalTrades.filter(t => !t.isLiquidation),
    [historicalTrades]
  );
  const historicalLiqs = useMemo(() =>
    historicalTrades.filter(t => t.isLiquidation),
    [historicalTrades]
  );

  // Combine and sort whale trades
  const displayWhales = useMemo(() => {
    const tradesMap = new Map();
    historicalWhales.forEach(t => {
      const key = `${t.exchange}-${t.symbol}-${t.timestamp}-${t.price}`;
      tradesMap.set(key, t);
    });
    liveWhaleTrades.forEach(t => {
      const key = `${t.exchange}-${t.symbol}-${t.timestamp}-${t.price}`;
      tradesMap.set(key, t);
    });
    const combined = Array.from(tradesMap.values());
    if (activeTab === 'best') {
      combined.sort((a, b) => b.dollarSize - a.dollarSize);
    } else {
      combined.sort((a, b) => {
        const aT = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
        const bT = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
        return bT - aT;
      });
    }
    return combined.slice(0, 4);
  }, [historicalWhales, liveWhaleTrades, activeTab]);

  // Combine and sort liquidations
  const displayLiqs = useMemo(() => {
    const liqsMap = new Map();
    historicalLiqs.forEach(t => {
      const key = `${t.exchange}-${t.symbol}-${t.timestamp}-${t.price}`;
      liqsMap.set(key, t);
    });
    liveLiquidations.forEach(t => {
      const key = `${t.exchange}-${t.symbol}-${t.timestamp}-${t.price}`;
      liqsMap.set(key, t);
    });
    const combined = Array.from(liqsMap.values());
    if (activeTab === 'best') {
      combined.sort((a, b) => b.dollarSize - a.dollarSize);
    } else {
      combined.sort((a, b) => {
        const aT = typeof a.timestamp === 'number' ? a.timestamp : new Date(a.timestamp).getTime();
        const bT = typeof b.timestamp === 'number' ? b.timestamp : new Date(b.timestamp).getTime();
        return bT - aT;
      });
    }
    return combined.slice(0, 4);
  }, [historicalLiqs, liveLiquidations, activeTab]);

  // Use real data or mock fallback while loading
  const hasRealData = displayWhales.length > 0 || displayLiqs.length > 0;
  const whales = hasRealData ? displayWhales : MOCK_TRADES.whales;
  const liqs = hasRealData ? displayLiqs : MOCK_TRADES.liqs;
  const btcPrice = prices.BTC || 67234.50;

  return (
    <div
      className={`wc-widget-frame theme-${theme}`}
      style={{ width: '100%', height: '100vh', borderRadius: 0, border: 'none' }}
    >
      <div className="wf-body">
        {/* LIVE DATA indicator */}
        <div className="wf-live-bar">
          <div className="wf-live-dot-wrap">
            <div className="wf-live-dot" />
            <span className="wf-live-text wf-dim">LIVE DATA</span>
          </div>
          <span className="wf-version wf-dim">HL_V5</span>
        </div>

        {/* BTC Price Card */}
        <div className="wf-price-card">
          <div className="wf-price-left">
            <BitcoinIcon size={20} />
            <span className="wf-price-symbol">BTC/USDT</span>
          </div>
          <div className="wf-price-right">
            <span className="wf-price-value">
              ${btcPrice.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="wf-tabs">
          <button
            onClick={() => setActiveTab('best')}
            className={`wf-tab ${activeTab === 'best' ? 'active' : ''}`}
          >
            Best Trade
          </button>
          <button
            onClick={() => setActiveTab('new')}
            className={`wf-tab ${activeTab === 'new' ? 'active' : ''}`}
          >
            New Trade
          </button>
        </div>

        {/* Whale Trades */}
        <div className="wf-section">
          <div className="wf-section-header">
            <span className="wf-section-title">Whale Trades</span>
            <div className="wf-section-line" />
          </div>
          {whales.map(t => (
            <div key={t.id || `${t.timestamp}-${t.dollarSize}`} className="wf-trade-row">
              <div className="wf-trade-amount">
                <span>{formatDollar(t.dollarSize)}</span>
                <InfoIcon size={10} />
                <div className="wf-tooltip">Entry: ${t.price?.toLocaleString()}</div>
              </div>
              <div className={`wf-trade-side ${tradeSide(t.side)}`}>{tradeSide(t.side).toUpperCase()}</div>
              <div className="wf-trade-time">{formatTimeAgo(t.timestamp)}</div>
            </div>
          ))}
        </div>

        {/* Liquidations */}
        <div className="wf-section">
          <div className="wf-section-header">
            <span className="wf-section-title">Liquidations</span>
            <div className="wf-section-line" />
          </div>
          {liqs.map(t => (
            <div key={t.id || `${t.timestamp}-${t.dollarSize}`} className="wf-trade-row">
              <div className="wf-trade-amount">
                <span>{formatDollar(t.dollarSize)}</span>
                <InfoIcon size={10} />
                <div className="wf-tooltip">Entry: ${t.price?.toLocaleString()}</div>
              </div>
              <div className={`wf-trade-side ${tradeSide(t.side)}`}>{tradeSide(t.side).toUpperCase()}</div>
              <div className="wf-trade-time">{formatTimeAgo(t.timestamp)}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Branding Footer */}
      <div className="wf-footer">
        <a href="https://tradermap.io" target="_blank" rel="noreferrer">
          Powered by <span>tradermap.io</span>
          <ExternalLinkIcon size={8} />
        </a>
        <div className="wf-footer-version">TRADERMAP_STABLE_FEED_V5.0</div>
      </div>
    </div>
  );
}

export default WidgetEmbedPage;
